/**
 * ProductDetail.tsx — F18 T18.5 (szczegóły towaru, read-only) — migracja na
 * silnik formatek (unifikacja-formatek): `RecursiveFormRenderer` +
 * `useKartotekaRenderField(productsEngineConfig, …)` zamiast bespoke tab-bara
 * + `ProductDataTab`.
 *
 * Zero zapisu: towary NIE mają endpointu POST/PATCH (źródło Symfonia, sync
 * jednokierunkowy) — `editable`/`isCreate` są na sztywno `false` w całym
 * drzewie, `onChange`/`onExtChange` to no-opy z wyjątkiem odczytu wartości
 * pól dodatkowych (`useRecordExtValues` — czyta `extValues` z odpowiedzi GET
 * towaru, nigdy nie zapisuje; kontrolki są i tak zawsze `disabled`, więc
 * `onExtChange` faktycznie nigdy się nie odpala).
 *
 * 18 pól core (kolejność seeda, `productsEngineConfig.tsx`) schodzi jako
 * liście `field` przez `renderField` — core przez config (`product_type`
 * custom-widget na sztywnej liście, reszta natywnie), ext przez
 * `ExtFieldValueInput`. „Pola dodatkowe" NIE są osobnym blokiem ani zakładką:
 * pole dodatkowe rysuje się wyłącznie jako zwykłe pole postawione w kreatorze
 * układu (nic nie jest doklejane); stary blok `ext_fields_view` /
 * `product_ext_fields_view` w zapisanym układzie nie ma renderera i rysuje się
 * jako nic. „Audit log" to placeholder (`audit_log_view`/`product_audit_view`)
 * dispatchowany przez `contextRenderers` po TYPIE elementu (nie po slugu
 * zakładki) — sekcja-capable: audit renderowałby się identycznie, gdyby
 * builder osadził go w sekcji zamiast osobnej zakładki (patrz
 * `ProductDetail.test.tsx`, „audit w sekcji").
 *
 * Layout: `useSystemModuleActiveLayout('products')` + `topUpDocumentLayout`
 * (top-up niczego nie dokleja). Seed
 * (`seed-system-modules.ts`) już tworzy pełny layout — top-up jest więc w
 * praktyce no-op (idempotentny), zabezpiecza tylko brak layoutu (świeży
 * tenant przed seedem).
 *
 * Przycisk SQL (klocek układu `sql_button`, contextRenderer): zadanie w tle na towarze. Formularz jest
 * tylko do podglądu, więc przycisk zawsze rusza od razu (bez zapisu przed zadaniem). Na czas zadania pasek
 * blokady, picker kategorii (jedyna edycja na karcie) nieaktywny, a zapis kategorii odrzucony 409
 * `RECORD_LOCKED_BY_RUN` daje czytelny komunikat (mirror ContractorDetail).
 *
 * Gatowane modułem `crm_core` (ModuleGuard — towar to encja rdzenia CRM, ADR-0078 §10).
 * Backend GET /api/products/:id.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import {
  type TopUpContext,
  topUpDocumentLayout,
} from '@/components/documents/engine/topUpDocumentLayout';
import { RecursiveFormRenderer } from '@/components/form/RecursiveFormRenderer';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { ModuleGuard } from '@/components/ModuleGuard';
import {
  type ProductEngineExtra,
  type ProductFormData,
  productsEngineConfig,
} from '@/components/products/productsEngineConfig';
import { RecordRunLockBanner } from '@/components/record-procedures/RecordRunLockBanner';
import {
  collectSqlButtonLabels,
  parseSqlButtonSettings,
} from '@/components/record-procedures/sql-button-settings';
import { SqlButton } from '@/components/record-procedures/SqlButton';
import { Skeleton } from '@/components/Skeleton';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { useToast } from '@/contexts/ToastContext';
import { usePermission } from '@/hooks/usePermission';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useRecordProcedureRuns } from '@/hooks/useRecordProcedureRuns';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';

const MODULE_SLUG = 'products';

interface ProductDetailDto {
  id: string;
  code: string | null;
  name: string;
  productType: string;
  barcode: string | null;
  pkwiu: string | null;
  cn: string | null;
  fiscalName: string | null;
  unit: string | null;
  vatRate: string | null;
  currencyCode: string;
  purchasePrice: string | null;
  basePrice: string | null;
  weight: string | null;
  category: string | null;
  categoryId: string | null;
  catalogPath: string | null;
  description: string | null;
  isActive: boolean;
  source: string;
  externalId: string | null;
  /** Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu. */
  extValues?: Record<string, unknown>;
}

/** Header formularza silnika nigdy nie zapisuje (brak `setForm` — `form` jest wyliczany z GET). */
const noopFormChange = () => {};

export function ProductDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState('data');
  const toast = useToast();
  const queryClient = useQueryClient();
  // Powrót na listę niesie jej kontekst (`?mi=`) — patrz `useWithMenuItem`.
  const withMenuItem = useWithMenuItem();

  const productQ = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const res = await api.get<{ product: ProductDetailDto }>(`/products/${id}`);
      return res.data.product;
    },
    enabled: !!id,
  });

  const product = productQ.data;

  const { layoutConfig, myRoleId, isSuperUser } = useSystemModuleActiveLayout(MODULE_SLUG);

  // Przycisk SQL (klocek `sql_button`): zadania wykonywane w tle na tym towarze. Formularz towaru jest
  // tylko do podglądu (bez tworzenia i bez „Edytuj"), więc przycisk zawsze rusza od razu — bez zapisu
  // przed zadaniem. Stan (aktywne zadanie + ostatnie per procedura) odpytywany co ~2 s tylko przy
  // aktywnym zadaniu; po jego końcu komunikat w rogu i odświeżenie rekordu (zapytanie mogło go zmienić).
  // Napisy przycisków z układu służą komunikatom i paskowi blokady (zadanie zna tylko nazwę procedury).
  const sqlButtonLabels = useMemo(() => collectSqlButtonLabels(layoutConfig), [layoutConfig]);
  const runs = useRecordProcedureRuns('product', id, {
    labels: sqlButtonLabels,
    onFinished: () => {
      void queryClient.invalidateQueries({ queryKey: ['product', id] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      void queryClient.invalidateQueries({ queryKey: ['system-module-audit', MODULE_SLUG, id] });
    },
  });
  // Prawo uruchomienia = prawo edycji towaru (`symfonia_integration.products.edit` w aktywnej firmie) —
  // to samo, które serwer sprawdza przy zapisie klasyfikacji. Bramka w panelu to podpowiedź (lista kodów
  // nie zna zawężeń); o odmowie rozstrzyga serwer.
  const productEditPermission = usePermission('symfonia_integration.products.edit');
  // Towar zablokowany na czas zadania przycisku SQL (decyzja „A”): aktywne zadanie albo właśnie wysyłane
  // zlecenie — edycja klasyfikacji (jedyna edytowalna rzecz na karcie) nieaktywna.
  const recordLocked = runs.active !== null || runs.starting;

  // Kategoria kartoteki — JEDYNE edytowalne pole towaru (reszta read-only z synca Symfonii).
  // Trzymana w lokalnym stanie (optymistyczny podgląd), synchronizowana z GET; zapis inline PATCH.
  const [categoryId, setCategoryId] = useState<string | null>(null);
  useEffect(() => {
    setCategoryId(product?.categoryId ?? null);
  }, [product?.categoryId]);

  const saveCategoryMutation = useMutation({
    mutationFn: async (next: string | null) => {
      await api.patch(`/products/${id}`, { categoryId: next });
    },
    onError: (err) => {
      setCategoryId(product?.categoryId ?? null); // revert optymizmu
      // Zapis odrzucony, bo na towarze trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „nie udało się zapisać”; odświeżony stan zadań pokaże pasek blokady.
      if (runs.handleLockedError(err)) return;
      toast.show({ variant: 'error', title: t('tenant.products.detail.category.saveError') });
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.products.detail.category.saved') });
      if (id) queryClient.invalidateQueries({ queryKey: ['product', id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  const productExtra: ProductEngineExtra = {
    saveCategory: (next) => {
      setCategoryId(next); // optymistyczny podgląd w pickerze
      saveCategoryMutation.mutate(next);
    },
    savingCategory: saveCategoryMutation.isPending,
    categoryLocked: recordLocked,
  };

  // Pola dodatkowe: wartości z odpowiedzi GET towaru (`product.extValues`, bez osobnego żądania).
  const { extValues, handleExtChange } = useRecordExtValues({
    recordId: id,
    serverExtValues: product?.extValues,
    serverVersion: productQ.dataUpdatedAt,
  });

  const form: ProductFormData = useMemo(
    () => ({
      code: product?.code ?? null,
      name: product?.name ?? '',
      productType: product?.productType ?? '',
      barcode: product?.barcode ?? null,
      pkwiu: product?.pkwiu ?? null,
      cn: product?.cn ?? null,
      fiscalName: product?.fiscalName ?? null,
      unit: product?.unit ?? null,
      vatRate: product?.vatRate ?? null,
      currencyCode: product?.currencyCode ?? '',
      purchasePrice: product?.purchasePrice ?? null,
      basePrice: product?.basePrice ?? null,
      weight: product?.weight ?? null,
      category: product?.category ?? null,
      categoryId,
      catalogPath: product?.catalogPath ?? null,
      description: product?.description ?? null,
      isActive: product?.isActive ?? false,
      source: product?.source ?? '',
    }),
    [product, categoryId],
  );

  const { renderField, isLoading: fieldsLoading } = useKartotekaRenderField(productsEngineConfig, {
    form,
    onChange: noopFormChange,
    extValues,
    onExtChange: handleExtChange,
    editable: false,
    isCreate: false,
    extra: productExtra,
  });

  // Top-up (bezwarunkowy, idempotentny) — syntetyzuje 'data' z pól core gdy brak
  // aktywnego layoutu w DB (seed go i tak tworzy — patrz nagłówek pliku).
  // kartoteka-blokify (2026-08-27): 'ext_fields'/'audit_log' USUNIĘTE z pageTabs —
  // wcześniej top-up dosypywał je bezwarunkowo przy każdym renderze (nawet po świadomym
  // usunięciu w builderze, bo brak zakładki o danym slugu ≠ brak configu), co łamało
  // portability. „Audit log" jest zasiany jako NORMALNY, usuwalny blok w domyślnym
  // (seedowanym) layoucie `products` (product_audit_view), mirror kontrahentów/faktur.
  // „Pola dodatkowe" nie mają bloku ani zakładki — top-up niczego nie dokleja.
  const topUpCtx = useMemo<TopUpContext>(
    () => ({
      pageTabs: [{ slug: 'data', title: t('tenant.products.detail.tabs.data'), sortOrder: 0 }],
      coreFieldSlugs: productsEngineConfig.coreFieldSlugs,
    }),
    [t],
  );
  const engineLayout = useMemo(
    () => topUpDocumentLayout(layoutConfig, topUpCtx),
    [layoutConfig, topUpCtx],
  );

  return (
    <ModuleGuard moduleCode="crm_core">
      <div className="space-y-4 p-6">
        <Link
          to={withMenuItem('/products')}
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('tenant.products.detail.back')}
        </Link>

        {productQ.isLoading && (
          <div className="py-8 text-center">
            <Spinner />
          </div>
        )}

        {!productQ.isLoading && !product && (
          <div className="py-8 text-center text-text-tertiary" data-testid="product-not-found">
            {t('tenant.products.detail.notFound')}
          </div>
        )}

        {product && (
          <>
            <header className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-text-primary">{product.name}</h1>
              {product.source === 'symfonia' && (
                <span className="rounded bg-accent-500/10 px-2 py-0.5 text-xs font-medium text-accent-500">
                  {t('tenant.products.fromSymfonia')}
                </span>
              )}
            </header>

            {/* Zadanie przycisku SQL na towarze (w kolejce lub w toku): klasyfikacji towaru nie da się
              zmienić, a picker kategorii jest nieaktywny; pasek znika po zakończeniu zadania. */}
            {runs.active ? (
              <RecordRunLockBanner
                run={runs.active}
                label={runs.labelFor(runs.active)}
                refreshFailed={runs.isError}
              />
            ) : null}

            {fieldsLoading ? (
              <div className="space-y-3" data-testid="product-fields-loading">
                <Skeleton className="h-32" />
                <Skeleton className="h-32" />
              </div>
            ) : (
              <RecursiveFormRenderer
                layoutConfig={engineLayout}
                activeTabId={tab}
                onTabChange={setTab}
                myRoleId={myRoleId}
                isSuperUser={isSuperUser}
                renderField={renderField}
                contextRenderers={{
                  // kartoteka-blokify (2026-08-27): nazwany blok per kartoteka
                  // (product_audit_view) — przenośny, usuwalny z buildera. Generyczny
                  // audit_log_view zostaje jako FALLBACK dla layoutów sprzed blokify
                  // (ten sam renderer). Bloków „Pola dodatkowe" nie ma — patrz nagłówek.
                  product_audit_view: () => (
                    <SystemModuleAuditTab
                      moduleSlug={MODULE_SLUG}
                      recordId={product.id}
                      testIdPrefix="product-audit"
                    />
                  ),
                  audit_log_view: () => (
                    <SystemModuleAuditTab
                      moduleSlug={MODULE_SLUG}
                      recordId={product.id}
                      testIdPrefix="product-audit"
                    />
                  ),
                  // Klocek „Przycisk SQL”: zadanie w tle na tym towarze. Formularz towaru jest tylko do
                  // podglądu, więc bez zapisu przed zadaniem — klik rusza od razu. Prawo: prawo edycji
                  // towaru (serwer i tak sprawdza). Identyfikatory testowe per klocek
                  // (`sql-button-<id klocka>`): strona może mieć kilka przycisków. Klocek bez procedury /
                  // napisu: nic.
                  sql_button: ({ element }) => {
                    const cfg = parseSqlButtonSettings(element.settings);
                    if (cfg === null) return null;
                    return (
                      <SqlButton
                        label={cfg.label}
                        procedureId={cfg.procedureId}
                        active={runs.active}
                        lastRun={runs.lastByProcedure.find(
                          (run) => run.procedureId === cfg.procedureId,
                        )}
                        canEdit={productEditPermission.allowed}
                        starting={runs.starting}
                        onStart={(procedureId) => runs.startRun(procedureId)}
                        testIdPrefix={`sql-button-${element.id}`}
                      />
                    );
                  },
                }}
              />
            )}
          </>
        )}
      </div>
    </ModuleGuard>
  );
}
